import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button, TextInput, Checkbox, InlineNotification } from '@carbon/react';
import { ArrowRight, ArrowLeft, View, ViewOff, CheckmarkFilled } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import OAuthButtons from '../components/OAuthButtons';
import './AuthPages.scss';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { signUp, signInWithGoogle, signInWithApple, isAuthenticated, loading: authLoading, error: authError } = useAuth();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const redirectAfterSignup = searchParams.get('redirect') || '';

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData((prev) => (prev.email ? prev : { ...prev, email: emailParam }));
    }
  }, [searchParams]);

  // OAuth handlers - simplified to match sensorpushweb pattern
  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      // OAuth will redirect, so we don't need to handle success here
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setOauthLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      await signInWithApple();
      // OAuth will redirect, so we don't need to handle success here
    } catch (err) {
      setError(err.message || 'Failed to sign in with Apple');
      setOauthLoading(false);
    }
  };

  const isDark = theme === 'dark';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    setError('');
  };

  const validateStep = () => {
    if (step === 0) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        setError('Please fill in all required fields');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        setError('Please enter a valid email');
        return false;
      }
    }
    if (step === 1) {
      if (!formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        return false;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setError('');
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const { data, error: signUpError } = await signUp(formData.email, formData.password);
    
    if (signUpError) {
      // code === 'email_exists' is surfaced specially (with a sign-in link).
      setError(signUpError.message || 'Failed to create account');
      setLoading(false);
      return;
    }
    
    if (data) {
      setSuccess(true);
      setLoading(false);
      // If email confirmation is disabled, redirect to dashboard or shipment invite link
      if (data.session) {
        const dest = redirectAfterSignup && redirectAfterSignup.startsWith('/')
          ? redirectAfterSignup
          : '/dashboard';
        navigate(dest);
      }
    }
  };

  const emailExists = error === 'An account with this email already exists.';


  const steps = ['Account', 'Security', 'Details'];

  if (success) {
    return (
      <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="signup-page">
        <div className="auth-page__container auth-page__container--centered">
          <div className="auth-card">
            <div className="auth-card__header">
              <CheckmarkFilled size={48} style={{ color: '#24a148', marginBottom: '16px' }} />
              <h2 className="auth-card__title">Check your email</h2>
              <p className="auth-card__subtitle" style={{ marginTop: '8px' }}>
                We've sent a confirmation link to <strong>{formData.email}</strong>
              </p>
            </div>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <p style={{ color: isDark ? '#c6c6c6' : '#525252', marginBottom: '24px' }}>
                Click the link in your email to activate your account. If you don't see it, check your spam folder.
              </p>
              <Button kind="primary" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="signup-page">
      <div className="auth-page__container auth-page__container--split">
        {/* Left Side - Promotional Section (same layout as Login: blue in light/dark) */}
        <div className="auth-page__promo">
          <div className="auth-page__promo-content">
            <h1 className="auth-page__promo-title">Start your free trial</h1>
            <h2 className="auth-page__promo-brand">ChillVerify</h2>
            <p className="auth-page__promo-subtitle">
              Join thousands of teams using ChillVerify for environmental monitoring
            </p>
            <ul className="auth-page__promo-feature-list">
              <li><CheckmarkFilled size={18} /> Real-time temperature monitoring</li>
              <li><CheckmarkFilled size={18} /> Smart alerts via SMS, push & email</li>
              <li><CheckmarkFilled size={18} /> AI-powered insights</li>
              <li><CheckmarkFilled size={18} /> Comprehensive reporting</li>
            </ul>
            <button
              type="button"
              className="auth-page__promo-link"
              onClick={() => navigate('/')}
            >
              Learn more
            </button>
          </div>
        </div>

        {/* Right Side - Signup Form (same as Login form section) */}
        <div className="auth-page__form-section">
        <div className="auth-card">
          <div className="auth-card__header">
            <h2 className="auth-card__title">Create Account</h2>
            <p className="auth-card__step-text">Step {step + 1} of 3</p>
          </div>

          {/* Step Indicator */}
          <div className="step-indicator">
            {steps.map((label, idx) => (
              <React.Fragment key={idx}>
                <div className={`step-indicator__item ${idx <= step ? 'step-indicator__item--active' : ''} ${idx < step ? 'step-indicator__item--complete' : ''}`}>
                  <div className="step-indicator__circle">
                    {idx < step ? <CheckmarkFilled size={14} /> : idx + 1}
                  </div>
                  <span className="step-indicator__label">{label}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`step-indicator__line ${idx < step ? 'step-indicator__line--active' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {(error || authError) && (
            emailExists ? (
              <div className="auth-card__notification" style={{
                padding: '12px 16px',
                marginBottom: '16px',
                borderRadius: '4px',
                backgroundColor: isDark ? '#2d1b00' : '#fff8e1',
                border: `1px solid #f1c21b`,
                color: isDark ? '#f4f4f4' : '#161616',
                fontSize: '0.875rem',
              }}>
                <strong style={{ display: 'block', marginBottom: '4px' }}>
                  {error}
                </strong>
                Would you like to{' '}
                <span
                  onClick={() => navigate('/login')}
                  className="auth-card__link"
                  style={{ cursor: 'pointer', fontWeight: 600 }}
                >
                  sign in
                </span>{' '}
                instead?
              </div>
            ) : (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error || authError}
              onClose={() => setError('')}
              className="auth-card__notification"
            />
            )
          )}

          <form onSubmit={handleSubmit} className="auth-card__form">
            {/* Step 1: Basic Info */}
            {step === 0 && (
              <div className="auth-card__fields">
                <TextInput
                  id="firstName"
                  name="firstName"
                  labelText="First Name *"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                <TextInput
                  id="lastName"
                  name="lastName"
                  labelText="Last Name *"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                />
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  labelText="Email *"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Step 2: Password */}
            {step === 1 && (
              <div className="auth-card__fields">
                <div className="auth-card__password-field">
                  <TextInput
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    labelText="Password *"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    helperText="Must be at least 8 characters"
                  />
                  <button
                    type="button"
                    className="auth-card__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <ViewOff size={18} /> : <View size={18} />}
                  </button>
                </div>
                <TextInput
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  labelText="Confirm Password *"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Step 3: Additional Details */}
            {step === 2 && (
              <div className="auth-card__fields">
                <TextInput
                  id="company"
                  name="company"
                  labelText="Company (Optional)"
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={handleChange}
                />
                <TextInput
                  id="phone"
                  name="phone"
                  type="tel"
                  labelText="Phone Number (Optional)"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                />
                <div className="auth-card__terms">
                  <Checkbox
                    id="agreeTerms"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={(_, { checked }) => setFormData({ ...formData, agreeTerms: checked })}
                  />
                  <label htmlFor="agreeTerms" className="auth-card__terms-label">
                    I agree to the{' '}
                    <Link to="/terms" className="auth-card__link" target="_blank">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="auth-card__link" target="_blank">Privacy Policy</Link>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="auth-card__actions">
              {step < 2 ? (
                <Button kind="primary" onClick={nextStep} className="auth-card__btn" renderIcon={ArrowRight}>
                  Continue
                </Button>
              ) : (
                <Button kind="primary" type="submit" className="auth-card__btn" disabled={loading || authLoading} renderIcon={ArrowRight}>
                  {loading || authLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              )}
              {step > 0 && (
                <Button kind="secondary" onClick={prevStep} className="auth-card__btn" renderIcon={ArrowLeft}>
                  Back
                </Button>
              )}
            </div>
          </form>

          {/* OAuth Signup - Only on first step */}
          {step === 0 && (
            <OAuthButtons
              onGoogleSignIn={handleGoogleSignIn}
              onAppleSignIn={handleAppleSignIn}
              isLoading={oauthLoading || loading || authLoading}
              mode="signup"
              showApple={true}
            />
          )}

          <p className="auth-card__footer">
            Already have an account?{' '}
            <span onClick={() => navigate('/login')} className="auth-card__link">Sign in</span>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
